import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

import { assessmentAdminError } from '../Assessments/api';
import { apiErrorFrom, ScenarioApiError } from './errors';
import {
  BenchmarkRollupRow,
  BlueprintCoverageRow,
  BlueprintFamilyOption,
  ComputeCommandResponse,
  ComputeLaneEvent,
  ComputeSnapshot,
  DraftVerdictsResponse,
  JobFilters,
  JobsResponse,
  JobSummary,
  JsonObject,
  Pagination,
  ProblemDocument,
  RegistryAssetRow,
  RegistryCoverageResponse,
  ScenarioDraftDetail,
  ScenarioDraftSummary,
  ScenarioJob,
  VerdictSummaryRow,
} from './types';

/**
 * Isolated client for the read-only scenario-builder admin endpoints
 * (pm/scenario_builder/admin_views.py). Same boundary discipline as the
 * assessment admin api.ts it is copied from: every call goes through
 * apiCaller so auth refresh and error shape stay uniform, and the error
 * flattener is shared so nested DRF errors read the same in both modules.
 *
 * The backend speaks two pagination dialects (verified against the live
 * endpoints): the drafts model viewset answers DRF page envelopes
 * ({count, next, previous, results}), while the computed-row endpoints
 * (benchmarks, registry coverage) answer the asset-mapping-review document
 * ({pagination, rows}). The normalizers below fold both — plus a bare
 * array, kept for test fixtures — into the screen-facing types.
 *
 * The control-plane calls (jobs, compute, problems) are the only writes in
 * this module, and they are deliberately narrow: submitting work is the
 * actions client's job, this one only lists, cancels, retries, and starts or
 * stops the compute lane. Two answers here are NOT errors and must never be
 * flattened into one — a 409 from the lane carries the snapshot (the lane
 * was already in that state, so the card re-renders), and a 503 carries the
 * named blockers of an environment that was never provisioned.
 */

/** DRF PageNumberPagination envelope (drafts list, verdicts sub-list). */
type DrfPage<Row> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Row[];
};

/** Asset-mapping-review style document (benchmarks, registry coverage). */
type RowsDocument<Row> = {
  pagination: Pagination;
  rows: Row[];
  [key: string]: unknown;
};

const isDrfPage = <Row>(value: unknown): value is DrfPage<Row> =>
  Boolean(value) &&
  typeof value === 'object' &&
  Array.isArray((value as DrfPage<Row>).results) &&
  typeof (value as DrfPage<Row>).count === 'number';

const isRowsDocument = <Row>(value: unknown): value is RowsDocument<Row> =>
  Boolean(value) &&
  typeof value === 'object' &&
  Array.isArray((value as RowsDocument<Row>).rows);

/**
 * Folds the three list shapes a paginated admin endpoint can answer with —
 * a DRF page envelope, an already-normalized {pagination, results}
 * document, or a bare array — into the one the screens page against. DRF
 * gives next/previous as URLs, so the page NUMBERS are derived from the
 * request that asked for them; a page the caller did not number is page 1.
 */
const normalizePage = <Row>(
  result: unknown,
  filters?: { page?: number; page_size?: number },
): { pagination: Pagination; results: Row[] } => {
  if (Array.isArray(result)) {
    return {
      pagination: {
        count: result.length,
        total_pages: 1,
        page: 1,
        page_size: result.length,
        next_page: null,
        previous_page: null,
      },
      results: result as Row[],
    };
  }
  if (isDrfPage<Row>(result)) {
    const page = filters?.page ?? 1;
    const pageSize = filters?.page_size ?? Math.max(result.results.length, 1);
    const totalPages = Math.max(1, Math.ceil(result.count / pageSize));
    return {
      pagination: {
        count: result.count,
        total_pages: totalPages,
        page,
        page_size: pageSize,
        next_page: result.next ? page + 1 : null,
        previous_page: result.previous ? page - 1 : null,
      },
      results: result.results,
    };
  }
  return result as { pagination: Pagination; results: Row[] };
};

/** The computed-row endpoints cap page_size at 500; the screens paginate
 *  client-side, so one full page is the whole catalogue today. */
const FULL_PAGE_SIZE = 500;
/** The drafts model viewset caps page_size at 200; pages are followed. */
const DRAFTS_PAGE_SIZE = 200;

const request = async <Result, Payload extends object = JsonObject>(
  path: string,
  data?: Payload,
): Promise<Result> => {
  const response = await apiCaller<Payload>({
    path,
    method: 'GET',
    data,
    apiUrl: API_URL,
  });
  if (response.status < 200 || response.status >= 300) {
    // The STATUS travels with the message: it is the only thing that can tell
    // "that draft was archived" from "the server fell over", and a screen
    // handed a bare string had to render the API envelope at the admin.
    throw apiErrorFrom(response.status, response.result);
  }
  return response.result as Result;
};

/**
 * The full draft ledger. The screen paginates and filters client-side (the
 * Assessments catalog pattern), so DRF pages are followed until exhausted —
 * 145 drafts today is one page of 200.
 */
export const listScenarioDrafts = async (filters?: {
  q?: string;
  status?: string;
}): Promise<ScenarioDraftSummary[]> => {
  const rows: ScenarioDraftSummary[] = [];
  for (let page = 1; ; page += 1) {
    const result = await request<
      DrfPage<ScenarioDraftSummary> | ScenarioDraftSummary[],
      { q?: string; status?: string; page: number; page_size: number }
    >('/scenario-builder/admin/drafts', {
      ...filters,
      page,
      page_size: DRAFTS_PAGE_SIZE,
    });
    if (Array.isArray(result)) return result;
    rows.push(...result.results);
    if (!result.next) return rows;
  }
};

/** verdict_counts {target: {verdict: n}} flattened into rows the verdict
 *  summary tags iterate. */
const verdictSummaryRows = (
  counts: Record<string, Record<string, number>> | undefined,
): VerdictSummaryRow[] =>
  Object.entries(counts || {}).flatMap(([target, verdicts]) =>
    Object.entries(verdicts || {}).map(([verdict, count]) => ({
      target,
      verdict,
      count,
    })),
  );

export const fetchScenarioDraft = async (
  id: number,
): Promise<ScenarioDraftDetail> => {
  const result = await request<
    ScenarioDraftDetail & {
      verdict_counts?: Record<string, Record<string, number>>;
    }
  >(`/scenario-builder/admin/drafts/${id}`);
  return {
    ...result,
    verdict_summary:
      result.verdict_summary ?? verdictSummaryRows(result.verdict_counts),
  };
};

/**
 * The paginated verdict sub-list. The live backend answers the DRF page
 * envelope; the asset-mapping document and a bare array are folded into the
 * same response so fixtures and shape drift both stay renderable.
 */
export const listDraftVerdicts = async (
  id: number,
  filters?: {
    target?: string;
    verdict?: string;
    page?: number;
    page_size?: number;
  },
): Promise<DraftVerdictsResponse> => {
  const result = await request<
    | DraftVerdictsResponse
    | DrfPage<DraftVerdictsResponse['results'][number]>
    | DraftVerdictsResponse['results'],
    NonNullable<typeof filters>
  >(`/scenario-builder/admin/drafts/${id}/verdicts`, filters);
  return normalizePage<DraftVerdictsResponse['results'][number]>(
    result,
    filters,
  );
};

/** One family row of the backend rollup document: the blueprint family, its
 *  provenance, and the assessment scenarios bound to it. */
type BenchmarkFamilyRow = {
  blueprint: { id: number; name: string; slug?: string; category?: string };
  family_tags: string[];
  serving_tags: string[];
  draft_id: number | null;
  minted_by_draft_ids: number[];
  versions: unknown[];
  scenarios: Array<
    Omit<BenchmarkRollupRow, 'blueprint' | 'minted_by_draft_id'>
  >;
};

/**
 * The rollup, flattened to the spec's per-AssessmentScenario rows. The
 * backend answers one row per tagged blueprint family with its scenarios
 * nested; each scenario inherits the family's blueprint identity and mint
 * provenance (the newest recorded mint; null for legacy families, which the
 * screen renders as NO RECORDED MINT).
 */
export const listBenchmarkRollup = async (): Promise<BenchmarkRollupRow[]> => {
  const result = await request<
    RowsDocument<BenchmarkFamilyRow> | BenchmarkRollupRow[],
    { page_size: number }
  >('/scenario-builder/admin/benchmarks', { page_size: FULL_PAGE_SIZE });
  if (Array.isArray(result)) return result;
  return result.rows.flatMap(family =>
    (family.scenarios || []).map(scenario => ({
      ...scenario,
      blueprint: { id: family.blueprint.id, name: family.blueprint.name },
      minted_by_draft_id: family.minted_by_draft_ids?.length
        ? family.minted_by_draft_ids[family.minted_by_draft_ids.length - 1]
        : null,
    })),
  );
};

/**
 * The four numbers the landing paragraph states.
 *
 * The module's own copy used to describe its plumbing ("Drafts, benchmark
 * rollups and registry coverage are inspection surfaces...") and never said
 * what the thing is FOR or how much of it there is. These are counted, not
 * estimated: the draft total comes from the ledger's own page envelope (one
 * row fetched, the count read off it) and the rest from the rollup the
 * Benchmarks tab already reads.
 */
export type FleetOverview = {
  drafts: number;
  families: number;
  answer_keys: number;
  live_answer_keys: number;
  passing_gate: number;
};

export const fetchFleetOverview = async (): Promise<FleetOverview> => {
  const [draftPage, rollup] = await Promise.all([
    request<
      DrfPage<ScenarioDraftSummary> | ScenarioDraftSummary[],
      { page: number; page_size: number }
    >('/scenario-builder/admin/drafts', { page: 1, page_size: 1 }),
    request<
      RowsDocument<BenchmarkFamilyRow> | BenchmarkRollupRow[],
      { page_size: number }
    >('/scenario-builder/admin/benchmarks', { page_size: FULL_PAGE_SIZE }),
  ]);
  const drafts = Array.isArray(draftPage) ? draftPage.length : draftPage.count;
  const families = Array.isArray(rollup) ? 0 : rollup.rows.length;
  const scenarios = Array.isArray(rollup)
    ? rollup
    : rollup.rows.flatMap(family => family.scenarios || []);
  return {
    drafts,
    families,
    answer_keys: scenarios.length,
    live_answer_keys: scenarios.filter(
      scenario => scenario.status_label === 'active',
    ).length,
    passing_gate: scenarios.filter(
      scenario =>
        String(
          (scenario as { validity?: { verdict?: string } }).validity?.verdict ||
            '',
        ).toLowerCase() === 'pass',
    ).length,
  };
};

/** Blueprint-mode document as the backend speaks it. */
type BlueprintCoverageDocument = {
  blueprint: { id: number; name: string; slug?: string };
  version: unknown;
  variant_id: number | null;
  template_session_id: number | null;
  scenarios: Array<{ id: number; code: string; status_label: string }>;
  rows: BlueprintCoverageRow[];
};

/**
 * Registry coverage. Without a blueprint the backend grades every registry
 * asset in the asset-mapping document ({pagination, rows} plus summary and
 * params_version); with ?blueprint=<id> it resolves the family's template
 * session into role rows. Both are folded into the mode-tagged response the
 * screen switches on; a bare array is normalized too, inferring the mode
 * from the requested filter.
 */
export const fetchRegistryCoverage = async (filters?: {
  blueprint?: number;
}): Promise<RegistryCoverageResponse> => {
  if (filters?.blueprint) {
    const result = await request<
      BlueprintCoverageDocument | BlueprintCoverageRow[],
      { blueprint: number }
    >('/scenario-builder/admin/registry-coverage', {
      blueprint: filters.blueprint,
    });
    if (Array.isArray(result)) {
      return { mode: 'blueprint', blueprint: null, results: result };
    }
    // The assessment link needs one scenario code; the newest active
    // binding wins (codes sort by version suffix), matching the rollup.
    const active = result.scenarios.filter(
      scenario => scenario.status_label === 'active',
    );
    const linked = active.length ? active : result.scenarios;
    return {
      mode: 'blueprint',
      blueprint: {
        id: result.blueprint.id,
        name: result.blueprint.name,
      },
      scenario_code: linked.length ? linked[linked.length - 1].code : null,
      results: result.rows,
    };
  }
  const result = await request<
    | (RowsDocument<RegistryAssetRow> & {
        summary?: RegistryCoverageResponse['summary'];
        params_version?: string;
      })
    | RegistryAssetRow[],
    { page_size: number }
  >('/scenario-builder/admin/registry-coverage', {
    page_size: FULL_PAGE_SIZE,
  });
  if (Array.isArray(result)) {
    return { mode: 'registry', blueprint: null, results: result };
  }
  if (isRowsDocument<RegistryAssetRow>(result)) {
    return {
      mode: 'registry',
      blueprint: null,
      results: result.rows,
      summary: result.summary,
      params_version: result.params_version,
    };
  }
  return result;
};

/**
 * The EXISTING blueprint family list (trainer/admin read), used only to
 * populate the coverage screen's blueprint picker — no new blueprint UI.
 */
export const listBlueprintFamilies = () =>
  request<BlueprintFamilyOption[]>('/blueprints');

/* ------------------------------------------------------------------ *
 * The control plane
 * ------------------------------------------------------------------ */

const ADMIN = '/scenario-builder/admin';

/** The ledger's page size. The screen filters server-side, so a page is a
 *  page — unlike the drafts list, this ledger only grows. */
export const JOBS_PAGE_SIZE = 25;

const post = async <Payload extends object = JsonObject>(
  path: string,
  data?: Payload,
) =>
  apiCaller<Payload>({
    path,
    method: 'POST',
    data: data || ({} as Payload),
    apiUrl: API_URL,
  });

/**
 * A 409 on a job command means another live job holds the target. The body
 * names it, so the message says which job, whose it is and when it started
 * — the second admin should read a fact, not a mystery.
 */
const jobConflictMessage = (body: unknown): string => {
  const live = (body as { live_job?: Record<string, unknown> })?.live_job;
  if (!live) return assessmentAdminError(body);
  const actor = live.created_by;
  const who =
    actor && typeof actor === 'object'
      ? String((actor as { username?: string }).username || 'another admin')
      : actor
        ? String(actor)
        : 'another admin';
  const when = live.started_at || live.created_at;
  const target = live.target_label ? ` for ${live.target_label}` : '';
  return (
    `${live.kind} is already ${String(live.status || 'running').toLowerCase()}` +
    `${target}, requested by ${who}${when ? ` at ${new Date(String(when)).toLocaleString()}` : ''}. ` +
    'Wait for it to finish, or cancel it first.'
  );
};

const jobCommand = async (id: string, action: string): Promise<ScenarioJob> => {
  const response = await post(`${ADMIN}/jobs/${id}/${action}`);
  if (response.status === 409) {
    throw new ScenarioApiError(
      jobConflictMessage(response.result),
      409,
      response.result,
    );
  }
  if (response.status < 200 || response.status >= 300) {
    throw apiErrorFrom(response.status, response.result);
  }
  return response.result as ScenarioJob;
};

/** One page of the job ledger, newest first, filtered server-side. */
export const fetchJobs = async (
  filters?: JobFilters,
): Promise<JobsResponse> => {
  const query = {
    page: 1,
    page_size: JOBS_PAGE_SIZE,
    ...(filters || {}),
  };
  const result = await request<unknown, typeof query>(`${ADMIN}/jobs`, query);
  return normalizePage<ScenarioJob>(result, query);
};

export const fetchJob = (id: string) =>
  request<ScenarioJob>(`${ADMIN}/jobs/${encodeURIComponent(id)}`);

/**
 * Live counts plus the refusals waiting on a human, grouped by the gate
 * that produced them. This is the "what needs attention" panel: 31 drafts
 * refusing for one named reason is one row with a remedy, not 31 incidents.
 */
export const fetchJobSummary = () =>
  request<JobSummary>(`${ADMIN}/jobs/summary`);

/** Only a PENDING or BLOCKED job can be canceled; a RUNNING one answers
 *  409, because cancelling mid-import is what the lease exists to clean up. */
export const cancelJob = (id: string) =>
  jobCommand(encodeURIComponent(id), 'cancel');

/** A retry is a NEW row (attempt + 1) linked to the one it supersedes, so
 *  the failed attempt stays in the audit. The new row comes back. */
export const retryJob = (id: string) =>
  jobCommand(encodeURIComponent(id), 'retry');

export const fetchCompute = () => request<ComputeSnapshot>(`${ADMIN}/compute`);

const computeCommand = async (
  action: 'start' | 'stop',
): Promise<ComputeCommandResponse> => {
  const response = await post(`${ADMIN}/compute/${action}`);
  if (response.status === 503) {
    // The server sends `ComputeError.as_payload()` — {code, message, remedy,
    // details} — with the lane snapshot beside it. Every one of those fields
    // is the difference between "restart the stack with
    // SCENARIO_CORPUS_ENABLED=1" and "this environment was never given a
    // scenario service", so all of them are carried through. Reading
    // `body.detail` (a key nothing sends) collapsed four distinct refusals
    // into one generic sentence and dropped every remedy with them.
    const body = (response.result || {}) as {
      code?: string;
      message?: string;
      remedy?: string;
      details?: { blockers?: string[] } & Record<string, unknown>;
      snapshot?: ComputeSnapshot;
      /** DRF's own key, in case a 503 ever arrives from elsewhere. */
      detail?: string;
    };
    const refused = (body.snapshot || null) as ComputeSnapshot | null;
    return {
      status: response.status,
      snapshot: refused,
      refusal: {
        code: body.code || 'compute_refused',
        message:
          body.message ||
          body.detail ||
          'Scenario compute is not available in this environment.',
        remedy: body.remedy || '',
        // A refusal names its own blockers when it has them
        // (`compute_unavailable`, `local_restart_required`); the ECS ones
        // carry an AWS code instead, and the lane's own blockers are then the
        // sentences an admin needs.
        blockers: body.details?.blockers || refused?.blockers || [],
        details: (body.details || {}) as Record<string, unknown>,
      },
    };
  }
  // 202 took the command; 409 means the lane was already in that state and
  // the body is the snapshot. Both re-render the card; neither is an error.
  if (
    response.status === 409 ||
    (response.status >= 200 && response.status < 300)
  ) {
    const result = (response.result || null) as
      | (ComputeSnapshot & { resent?: boolean })
      | null;
    return {
      status: response.status,
      snapshot: result,
      refusal: null,
      // A stop the row already believed had happened, sent to the platform
      // anyway because it was still holding a task. Not the same news as
      // "nothing changed", and the card says which.
      resent: Boolean(result?.resent),
    };
  }
  throw apiErrorFrom(response.status, response.result);
};

export const startCompute = () => computeCommand('start');
export const stopCompute = () => computeCommand('stop');

/** One row per state transition — never per heartbeat. */
export const fetchComputeEvents = async (filters?: {
  page?: number;
  page_size?: number;
}): Promise<{ pagination: Pagination; results: ComputeLaneEvent[] }> => {
  const query = { page: 1, page_size: 50, ...(filters || {}) };
  const result = await request<unknown, typeof query>(
    `${ADMIN}/compute/events`,
    query,
  );
  return normalizePage<ComputeLaneEvent>(result, query);
};

/**
 * The fleet problem document. A bare array is folded into the envelope so a
 * shape change cannot blank the screen: the counts are then derived from the
 * rows rather than trusted.
 */
export const fetchProblems = async (): Promise<ProblemDocument> => {
  const result = await request<ProblemDocument | ProblemDocument['problems']>(
    `${ADMIN}/problems`,
  );
  if (Array.isArray(result)) {
    return {
      checked_at: new Date().toISOString(),
      counts: {
        error: result.filter(row => row.severity === 'error').length,
        warn: result.filter(row => row.severity === 'warn').length,
      },
      problems: result,
    };
  }
  return {
    checked_at: result.checked_at,
    counts: result.counts || { error: 0, warn: 0 },
    problems: result.problems || [],
  };
};
